"use client"

import { useEffect, useId, useState } from "react"
import { BlogFAQSection } from "@/components/ui/blog-faq-section"
import { HelpCircle, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import type { FAQItem } from "@/lib/content-analyzers"

interface LazyFAQSectionProps {
  initialFaqs?: FAQItem[]
}

// The FAQ list must render exactly ONCE, with the mobile/desktop split done
// in CSS (max-md:* / md:*): a second DOM copy reads as a duplicate block to
// crawlers and FAQ schema, and a JS/width-driven split diverges SSR from
// client, causing a hydration-time height shift in the ad-injection band.
export function LazyFAQSection({
  initialFaqs = [],
}: LazyFAQSectionProps) {
  const faqs = initialFaqs
  const [isExpanded, setIsExpanded] = useState(false)
  const panelId = useId()
  // The collapse is CSS-only, so the hidden panel's buttons stay in the
  // accessibility tree unless it is also inert. Inert must not apply on
  // desktop (where CSS shows the panel), and it must not diverge SSR from
  // client, so it is set post-hydration from the same breakpoint the CSS uses.
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    const update = () => setIsMobile(mq.matches)
    update()
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])

  // FAQ generation belongs to the admin workflow; public pages should only
  // render FAQs that were already stored with the post or guide.
  if (faqs.length === 0) {
    return null
  }

  return (
    <div className="mt-8 md:mt-12">
      {/* Mobile: glass shell + collapse toggle. `md:contents` removes the shell's
          box on desktop so the FAQ card is not double-framed. The glass classes
          must be mobile-scoped too: `display: contents` drops the shell's
          `position: relative`, but `ds-glass::before` (absolute, inset-0) still
          renders and would anchor to the page wrapper, painting its white sheen
          over the whole article. */}
      <div className="max-md:ds-glass max-md:rounded-[18px] max-md:overflow-hidden md:contents">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-4 bg-white/55 backdrop-blur-md border-b border-white/55 hover:bg-white/70 transition-colors md:hidden"
          aria-expanded={isExpanded}
          aria-controls={panelId}
        >
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-2xl border border-white/70 bg-green-50/85 text-green-700">
              <HelpCircle className="w-5 h-5" />
            </div>
            <div className="text-left">
              <h2 className="font-editorial font-bold text-gray-900">Frequently Asked Questions</h2>
              <p className="text-sm text-gray-500">{faqs.length} questions</p>
            </div>
          </div>
          <ChevronDown className={cn(
            "w-5 h-5 text-green-700 transition-transform duration-300",
            isExpanded && "rotate-180"
          )} />
        </button>

        <div
          id={panelId}
          inert={isMobile && !isExpanded ? true : undefined}
          className={cn(
            "max-md:overflow-hidden transition-all duration-300 ease-out",
            isExpanded
              ? "max-md:max-h-[5000px] max-md:opacity-100"
              : "max-md:max-h-0 max-md:opacity-0"
          )}>
          <BlogFAQSection
            faqs={faqs}
            hideHeaderOnMobile={true}
            className="max-md:border-0 max-md:shadow-none max-md:rounded-none max-md:bg-transparent"
          />
        </div>
      </div>
    </div>
  )
}
